import { useEffect, useRef, useState } from 'react'

import { useTexte } from '../../texte'
import type { Grossmedium } from '../../api/types'
import { notiere } from '../../lib/sendeprotokoll'
// **The one viewer for the app and the book** (1.173.0): the EPUB carries the
// same file as a script (`EpubBook::panoScript()`), so it lives beside the
// book on the server and is bundled from there.
import { mountPano, type PanoHandle } from '../../../../server/src/Book/panoviewer.js'

/**
 * A 360° photo in the full view (1.173.0, #154): drag to look around, two
 * fingers or the wheel to zoom, and on request the device turns the view.
 *
 * **Without WebGL, or when the picture cannot be drawn, it stays a flat
 * picture with a sentence** -- never an empty screen; the reason goes into
 * the diagnosis, like a failed terrain in the route film.
 *
 * The gestures belong to the viewer: it stops them at its canvas, and the
 * full view leaves `.lightbox__pano` alone (`Gallery.tsx`), so a drag turns
 * the view instead of paging. The arrows of the full view still page.
 */
export function PanoView({ media }: { media: Grossmedium }) {
  const t = useTexte()
  const words = t.chat.pano
  const host = useRef<HTMLDivElement>(null)
  const handle = useRef<PanoHandle | null>(null)
  const [state, setState] = useState<'loading' | 'ready' | 'failed' | 'unsupported'>('loading')
  const [motion, setMotion] = useState(false)
  const [refused, setRefused] = useState(false)

  useEffect(() => {
    const element = host.current
    if (element === null) return
    setState('loading')
    const mounted = mountPano(element, media.url, {
      onState: (next) => {
        if (next === 'failed') notiere(`360°-Foto ${media.id}: Bild nicht gezeichnet`)
        setState(next)
      },
    })
    if (mounted === null) {
      notiere(`360°-Foto ${media.id}: kein WebGL, flach gezeigt`)
      setState('unsupported')
      return
    }
    handle.current = mounted
    return () => {
      mounted.destroy()
      handle.current = null
      setMotion(false)
    }
  }, [media.id, media.url])

  const flat = state === 'failed' || state === 'unsupported'
  return (
    <div className="panoview" data-pano-view={state}>
      <div className="panoview__flaeche" ref={host} hidden={flat} />
      {flat && (
        <div className="panoview__flach">
          <img src={media.url} alt="" />
          <p className="panoview__hinweis" role="alert">{words.nichtRundum}</p>
        </div>
      )}
      {state === 'loading' && <p className="panoview__hinweis panoview__hinweis--laedt">{words.laedt}</p>}
      {state === 'ready' && handle.current?.motionAvailable() && (
        <button
          type="button"
          className="panoview__bewegung"
          aria-pressed={motion}
          data-pano-bewegung=""
          // Called inside the tap: iOS asks for the motion sensors only there.
          onClick={() => {
            const current = handle.current
            if (current === null) return
            const wanted = !motion
            void current.setMotion(wanted).then((on) => {
              setMotion(on)
              setRefused(wanted && !on)
            })
          }}
        >
          {motion ? words.bewegungAus : words.bewegungAn}
        </button>
      )}
      {refused && <p className="panoview__hinweis" role="alert">{words.bewegungAbgelehnt}</p>}
    </div>
  )
}
